import config from '../config/environment';

export const initialCameraState = { isConnecting: true, isConnected: false, failed: false, error: null, mediaStream: null };
const sessionKey = (streamId, streamUrl) => JSON.stringify([String(streamId), streamUrl]);

function createCameraSession(streamUrl) {
  let state = initialCameraState;
  let pc = null;
  let retryTimer = null;
  let connectionTimer = null;
  let cancelGathering = null;
  let request = null;
  let attempt = 0;
  let retryCount = 0;
  let disposed = false;
  let started = false;
  const listeners = new Set();
  const update = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener(state));
  };

  const closeTransport = () => {
    attempt += 1;
    clearTimeout(connectionTimer);
    clearTimeout(retryTimer);
    retryTimer = null;
    cancelGathering?.();
    cancelGathering = null;
    request?.abort();
    request = null;
    if (pc) {
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.onicegatheringstatechange = null;
      pc.close();
      pc = null;
    }
    state.mediaStream?.getTracks().forEach((track) => track.stop?.());
  };

  const scheduleRetry = () => {
    if (disposed) return;
    if (retryCount >= 20) {
      update({ isConnecting: false, failed: true });
      return;
    }
    retryCount += 1;
    update({ isConnecting: true });
    retryTimer = setTimeout(() => {
      retryTimer = null;
      startStream();
    }, Math.min(10000, 500 * Math.pow(2, retryCount - 1)));
  };

  const fail = (error) => {
    closeTransport();
    update({ isConnected: false, mediaStream: null, error: error.message });
    scheduleRetry();
  };

  const startStream = async () => {
    // Prevent multiple peer connections for the same stream instance
    if (pc || disposed) return;
    const currentAttempt = ++attempt;
    update({ isConnecting: true, failed: false, error: null });
    try {
      if (!streamUrl) throw new Error('Camera URL is not configured');
      // Create RTCPeerConnection
      const peer = new RTCPeerConnection({ iceServers: config.ICE_SERVERS });
      pc = peer;
      connectionTimer = setTimeout(() => fail(new Error('Camera connection timed out')), 20000);

      // Handle incoming stream
      peer.ontrack = (event) => {
        if (event.streams[0]) update({ mediaStream: event.streams[0] });
      };

      // Handle connection state changes
      peer.onconnectionstatechange = () => {
        if (peer.connectionState === 'connected') {
          clearTimeout(connectionTimer);
          update({ isConnected: true, isConnecting: false, failed: false, error: null });
          // Reset retry counter on successful connection
          retryCount = 0;
          clearTimeout(retryTimer);
          retryTimer = null;
        } else if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') {
          // Fully stop current stream and schedule a retry
          fail(new Error('Connection failed or disconnected'));
        }
      };

      // Handle ICE connection state changes
      peer.oniceconnectionstatechange = () => {
        if (pc === peer && peer.iceConnectionState === 'failed') fail(new Error('ICE connection failed'));
      };

      // Handle ICE gathering state changes
      peer.onicegatheringstatechange = () => {
        if (peer.iceGatheringState === 'complete') cancelGathering?.();
      };

      // Add transceiver for receiving video
      peer.addTransceiver('video', { direction: 'recvonly' });
      peer.addTransceiver('audio', { direction: 'recvonly' });

      // Create offer
      const offer = await peer.createOffer();
      if (currentAttempt !== attempt) return;
      await peer.setLocalDescription(offer);
      if (currentAttempt !== attempt) return;

      // Wait for ICE gathering to complete so localDescription contains srflx candidates
      // (helps significantly for public-network connectivity without TURN)
      if (peer.iceGatheringState !== 'complete') {
        await new Promise((resolve) => {
          let timer;
          const finish = () => {
            clearTimeout(timer);
            peer.removeEventListener('icegatheringstatechange', onStateChange);
            cancelGathering = null;
            resolve();
          };
          const onStateChange = () => { if (peer.iceGatheringState === 'complete') finish(); };
          timer = setTimeout(finish, 2000);
          cancelGathering = finish;
          peer.addEventListener('icegatheringstatechange', onStateChange);
        });
      }
      if (currentAttempt !== attempt) return;

      // Encode SDP to base64 for the /stream endpoint
      const sdp64 = btoa(peer.localDescription.sdp);
      request = new AbortController();
      // Send offer to Go backend using the /stream endpoint
      const response = await fetch(`${config.GO_BACKEND_URL}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ url: streamUrl, sdp64 }),
        signal: request.signal,
      });
      if (currentAttempt !== attempt) return;
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      // The /stream endpoint returns JSON with base64 encoded SDP
      const responseData = await response.json();
      if (currentAttempt !== attempt) return;
      if (!responseData.sdp64) throw new Error('No SDP answer received from server');
      const answerSdp = atob(responseData.sdp64);
      // Set remote description
      await peer.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (error) {
      if (disposed || currentAttempt !== attempt) return;
      // Clean up and schedule a bounded retry
      fail(error);
    }
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      if (!started) {
        started = true;
        // Small delay to ensure component is ready
        retryTimer = setTimeout(startStream, 0);
      }
      return () => listeners.delete(listener);
    },
    start() {
      retryCount = 0;
      clearTimeout(retryTimer);
      retryTimer = null;
      startStream();
    },
    stop() {
      closeTransport();
      update({ isConnecting: false, isConnected: false, failed: false, error: null, mediaStream: null });
    },
    dispose() {
      disposed = true;
      closeTransport();
      listeners.clear();
    },
  };
}

export function createCameraSessionPool() {
  const sessions = new Map();
  const remove = (key) => {
    const entry = sessions.get(key);
    if (!entry) return;
    clearTimeout(entry.releaseTimer);
    entry.session.dispose();
    sessions.delete(key);
  };
  return {
    acquire(streamId, streamUrl, listener) {
      const key = sessionKey(streamId, streamUrl);
      if (!sessions.has(key)) sessions.set(key, { session: createCameraSession(streamUrl), users: 0, releaseTimer: null });
      const entry = sessions.get(key);
      clearTimeout(entry.releaseTimer);
      entry.users += 1;
      const unsubscribe = entry.session.subscribe(listener);
      return {
        start: entry.session.start,
        stop: entry.session.stop,
        release() {
          unsubscribe();
          entry.users -= 1;
          if (!entry.users && sessions.get(key) === entry) entry.releaseTimer = setTimeout(() => {
            if (sessions.get(key) === entry) remove(key);
          }, 1500);
        },
      };
    },
    retain(streams) {
      const valid = new Set(streams.map((s) => sessionKey(s.id, s.url)));
      sessions.forEach((_, key) => { if (!valid.has(key)) remove(key); });
    },
    dispose() { [...sessions.keys()].forEach(remove); },
  };
}
